import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

const API_URL = '/api';

function HomeDashboard() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkStats, setNetworkStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [cpuHistory, setCpuHistory] = useState([]);
  const [coreHistory, setCoreHistory] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const cpuHistoryRef = useRef([]);
  const coreHistoryRef = useRef([]);
  const prevCacheRef = useRef(0);

  const loadAccount = useCallback(async (hash) => {
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: hash })
      });
      const data = await res.json();
      if (data.success) {
        if (data.data.account_type === 'server' || !data.data.account_type) {
          navigate('/dashboard');
          return;
        }
        setAccount({ account_hash: hash, ...data.data });

        // Track history
        const cpuVal = data.data.system_info?.cpu_usage || 0;
        const coreVal = data.data.core_hours_total || 0;
        const cacheVal = data.data.total_cache_served || 0;
        cpuHistoryRef.current = [...cpuHistoryRef.current.slice(-11), cpuVal];
        coreHistoryRef.current = [...coreHistoryRef.current.slice(-11), coreVal];
        setCpuHistory([...cpuHistoryRef.current]);
        setCoreHistory([...coreHistoryRef.current]);

        // Generate activity log entries when cache increases
        if (cacheVal > prevCacheRef.current && prevCacheRef.current > 0) {
          const diff = cacheVal - prevCacheRef.current;
          const actions = ['Cache HIT', 'Query served', 'Search cached', 'Result delivered'];
          const newEntries = [];
          for (let i = 0; i < Math.min(diff, 3); i++) {
            newEntries.push({
              id: Date.now() + i,
              action: actions[Math.floor(Math.random() * actions.length)],
              time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
              type: 'cache'
            });
          }
          if (newEntries.length > 0) {
            setActivityLog(prev => [...newEntries, ...prev].slice(0, 15));
          }
        }
        prevCacheRef.current = cacheVal;
      } else {
        localStorage.removeItem('avalw_account_hash');
        navigate('/login');
      }
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [navigate]);

  const loadNetworkStats = async () => {
    try {
      const res = await fetch(`${API_URL}/stats`);
      const data = await res.json();
      setNetworkStats({
        online: data.network?.workers_online || data.active_workers || 0,
        cores: data.network?.total_cores || 0,
        cache: data.lifetime?.cache_served || 0,
        co2: data.co2_impact?.saved_total_kg || 0
      });
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/leaderboard?limit=5`);
      const data = await res.json();
      if (data.success) setLeaderboard(data.leaderboard);
    } catch (err) {
      console.error('Leaderboard error:', err);
    }
  };

  useEffect(() => {
    const hash = localStorage.getItem('avalw_account_hash');
    if (!hash) {
      navigate('/login');
      return;
    }
    loadAccount(hash);
    loadNetworkStats();
    loadLeaderboard();

    const interval = setInterval(() => {
      loadAccount(hash);
      loadNetworkStats();
    }, 5000);

    return () => clearInterval(interval);
  }, [navigate, loadAccount]);

  const formatNum = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toLocaleString();
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="loader-spinner"></div>
          <span>Loading Dashboard...</span>
        </div>
      </AppLayout>
    );
  }

  const displayName = account?.profile?.display_name || 'Contributor';
  const uptimeHours = account?.uptime_hours || 0;
  const coreHours = account?.core_hours_total || 0;
  const userCO2 = account?.co2_saved_kg || 0;
  const userCache = account?.total_cache_served || 0;
  const cpuUsage = account?.system_info?.cpu_usage || 0;
  const contributionScore = Math.round(uptimeHours * 10 + coreHours + userCache / 1000);

  // Chart helpers
  const maxCpu = Math.max(...cpuHistory, 100);
  const maxCore = Math.max(...coreHistory, 1);

  return (
    <AppLayout>
      <div className="page-content dashboard-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <h1>Dashboard</h1>
            <p className="page-subtitle">Welcome back, {displayName}</p>
          </div>
          <div className="page-header-right">
            <div className="header-stat">
              <span className="header-stat-label">Global Rank</span>
              <span className="header-stat-value">#{account?.leaderboard_rank || '—'}</span>
            </div>
            <div className="header-stat">
              <span className="header-stat-label">Score</span>
              <span className="header-stat-value">{formatNum(contributionScore)}</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{formatNum(uptimeHours)}</span>
              <span className="stat-label">Uptime Hours</span>
            </div>
            <span className="stat-trend positive">+{(uptimeHours / 24).toFixed(1)}d</span>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{formatNum(coreHours)}</span>
              <span className="stat-label">Core Hours</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{userCO2.toFixed(2)}</span>
              <span className="stat-label">kg CO2 Saved</span>
            </div>
            <span className="stat-badge eco">ECO</span>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.89 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">{formatNum(userCache)}</span>
              <span className="stat-label">Cache Served</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="content-grid">
          <div className="content-main">
            {/* Performance Overview */}
            <div className="card">
              <div className="card-header">
                <h3>Performance Overview</h3>
                <span className="live-badge">
                  <span className="live-dot"></span>
                  Live
                </span>
              </div>
              <div className="charts-grid">
                <div className="chart-item">
                  <div className="chart-header">
                    <span className="chart-value">{cpuUsage.toFixed(0)}</span>
                    <span className="chart-label">CPU Usage</span>
                    <span className="chart-unit">{cpuUsage.toFixed(0)}%</span>
                  </div>
                  <div className="mini-chart">
                    <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      {cpuHistory.length > 1 && (
                        <>
                          <path
                            d={`M0,${60 - (cpuHistory[0] / maxCpu) * 55} ${cpuHistory.map((v, i) => `L${(i / (cpuHistory.length - 1)) * 200},${60 - (v / maxCpu) * 55}`).join(' ')} L200,60 L0,60 Z`}
                            fill="url(#cpuGrad)"
                          />
                          <path
                            d={`M0,${60 - (cpuHistory[0] / maxCpu) * 55} ${cpuHistory.map((v, i) => `L${(i / (cpuHistory.length - 1)) * 200},${60 - (v / maxCpu) * 55}`).join(' ')}`}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                          />
                        </>
                      )}
                    </svg>
                  </div>
                </div>

                <div className="chart-item">
                  <div className="chart-header">
                    <span className="chart-value">{formatNum(coreHours)}</span>
                    <span className="chart-label">Core Hours</span>
                    <span className="chart-unit">{formatNum(coreHours)}</span>
                  </div>
                  <div className="mini-chart">
                    <svg viewBox="0 0 200 60" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="coreGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      {coreHistory.length > 1 && (
                        <>
                          <path
                            d={`M0,${60 - (coreHistory[0] / maxCore) * 55} ${coreHistory.map((v, i) => `L${(i / (coreHistory.length - 1)) * 200},${60 - (v / maxCore) * 55}`).join(' ')} L200,60 L0,60 Z`}
                            fill="url(#coreGrad)"
                          />
                          <path
                            d={`M0,${60 - (coreHistory[0] / maxCore) * 55} ${coreHistory.map((v, i) => `L${(i / (coreHistory.length - 1)) * 200},${60 - (v / maxCore) * 55}`).join(' ')}`}
                            fill="none"
                            stroke="#8b5cf6"
                            strokeWidth="2"
                          />
                        </>
                      )}
                    </svg>
                  </div>
                </div>
              </div>

              {/* Activity Log */}
              <div className="activity-log">
                <div className="activity-log-header">
                  <span className="activity-log-title">Activity Log</span>
                  <span className="activity-log-count">{activityLog.length} events</span>
                </div>
                <div className="activity-log-list">
                  {activityLog.length === 0 ? (
                    <div className="activity-log-empty">
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>
                      <span>Waiting for activity...</span>
                    </div>
                  ) : (
                    activityLog.map((entry) => (
                      <div key={entry.id} className="activity-log-item">
                        <span className="activity-log-icon cache">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
                        </span>
                        <span className="activity-log-action">{entry.action}</span>
                        <span className="activity-log-time">{entry.time}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Environmental Impact */}
            <div className="card eco-card">
              <div className="card-header">
                <h3>Environmental Impact</h3>
                <Link to="/rewards" className="card-link">View Rewards</Link>
              </div>
              <div className="eco-content">
                <div className="eco-stat main">
                  <span className="eco-value">{userCO2.toFixed(2)}</span>
                  <span className="eco-unit">kg</span>
                  <span className="eco-label">CO2 Saved</span>
                </div>
                <div className="eco-details">
                  <div className="eco-detail">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
                    <span className="eco-detail-value">{(userCO2 / 0.021).toFixed(0)}</span>
                    <span className="eco-detail-label">Trees Equivalent (yearly)</span>
                  </div>
                  <div className="eco-detail">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
                    <span className="eco-detail-value">{(userCO2 / 0.21).toFixed(1)}</span>
                    <span className="eco-detail-label">km Car Offset</span>
                  </div>
                  <div className="eco-detail">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/></svg>
                    <span className="eco-detail-value">{(userCO2 * 2.5).toFixed(2)}</span>
                    <span className="eco-detail-label">kWh Energy Saved</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="content-sidebar">
            {/* Network Status */}
            <div className="card">
              <div className="card-header">
                <h3>Network Status</h3>
                <Link to="/network" className="card-link">View Live</Link>
              </div>
              <div className="network-stats">
                <div className="network-stat">
                  <span className="network-value online">{networkStats?.online || 0}</span>
                  <span className="network-label">Contributors Online</span>
                </div>
                <div className="network-stat">
                  <span className="network-value">{networkStats?.cores || 0}</span>
                  <span className="network-label">Total Cores</span>
                </div>
                <div className="network-stat">
                  <span className="network-value">{formatNum(networkStats?.cache || 0)}</span>
                  <span className="network-label">Total Queries</span>
                </div>
              </div>
            </div>

            {/* Top Contributors */}
            <div className="card">
              <div className="card-header">
                <h3>Top Contributors</h3>
                <Link to="/leaderboard" className="card-link">View All</Link>
              </div>
              <div className="leaderboard-mini">
                {leaderboard.map((user, index) => {
                  const isMe = user.account_hash === account?.account_hash || user.full_hash === account?.account_hash;
                  return (
                    <div key={user.id || index} className={`leader-item ${isMe ? 'is-me' : ''}`}>
                      <span className="leader-pos">{index + 1}</span>
                      <div className="leader-avatar" style={{ background: user.badge?.color || '#6366f1' }}>
                        {(user.display_name || 'A')[0]}
                      </div>
                      <div className="leader-info">
                        <span className="leader-name">{user.display_name || 'Anonymous'}</span>
                      </div>
                      <span className="leader-score">{formatNum(user.contribution_score || 0)} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default HomeDashboard;
